import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { COURSE_STATUS } from 'modules/host-learning-center/data/statusConstants';

import { CourseBody, QuizResult } from '../useCoursesStatus/model';
import { getCourseStatus, setStartCourse, updateCourse } from './clients';

function useSingleCourseStatus(id: string, accountId: string) {
  const queryClient = useQueryClient();

  const queryData = useQuery(
    ['courseStatus', accountId + id],
    () => getCourseStatus(id, accountId),
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      refetchOnMount: false,
      retry: 0,
    },
  );

  const mutation = useMutation(
    (body: CourseBody) => updateCourse(body, accountId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['courseStatus', accountId + id], {
          refetchType: 'inactive',
        });
        queryClient.setQueryData(['courseStatus', accountId + id], () => data);
      },
    },
  );

  const startMutation = useMutation(
    (body: CourseBody) => setStartCourse(body, accountId),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries(['courseStatus', accountId + id], {
          refetchType: 'inactive',
        });
        queryClient.setQueryData(['courseStatus', accountId + id], () => data);
      },
    },
  );

  const onCourseStart = async (courseId, course) => {
    const body: CourseBody = {
      courseId,
      name: course?.name,
      tier: course?.tier,
      steps: course.steps.map((step) => ({
        category: step.type,
        id: step.uuid,
        name: step.title,
        status: {
          type: COURSE_STATUS.PRISTINE,
        },
      })),
      status: {
        type: COURSE_STATUS.IN_PROGRESS,
      },
    };
    await startMutation.mutateAsync(body);
  };

  const onCompleteCourseStep = async (stepId: string) => {
    const result = queryData.data;
    const steps = result?.steps ?? [];

    const body: CourseBody = {
      ...result,
      steps: steps.map((step) => ({
        ...step,
        status: {
          type: stepId === step.id ? COURSE_STATUS.COMPLETED : step.status.type,
        },
      })),
    };
    await mutation.mutateAsync(body);
  };

  const onFinishCourse = async (quizResults: QuizResult[]) => {
    const result = queryData.data;

    const body: CourseBody = {
      ...result,
      quizResults,
      status: {
        type: COURSE_STATUS.COMPLETED,
      },
    };
    await mutation.mutateAsync(body);
  };

  return {
    ...queryData,
    actions: {
      onCourseStart,
      onCompleteCourseStep,
      onFinishCourse,
    },
  };
}

export default useSingleCourseStatus;
